import { useEffect, useRef } from 'react';
import { minimumTierForFeature } from '@bower/shared/billing/policy';
import { tierIncludesFeature } from '@bower/shared/billing/feature-registry';
import type { GateLayer, SubscriptionTier, TierFeature } from '@bower/shared';
import { useWorkspacePlan } from '../data/useBilling';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

export interface FeatureGateResult {
  /** True when the current tier includes the feature. */
  readonly allowed: boolean;
  /** Workspace's current tier. Defaults to FREE while the plan loads. */
  readonly currentTier: SubscriptionTier;
  /** Lowest tier that turns the feature on (for the upgrade prompt). */
  readonly requiredTier: SubscriptionTier | null;
  /** True while the billing plan is fetching. */
  readonly isLoading: boolean;
}

interface UseCanUseFeatureOptions {
  /**
   * When false, suppresses the `Feature Gate Hit` analytics event.
   * Use this for passive render-time checks (e.g. conditional UI visibility)
   * where a separate click-time signal already captures the intent.
   * Defaults to true.
   */
  track?: boolean;
}

/**
 * Returns whether the current workspace's tier includes `feature`, plus
 * the upgrade target. Reads from the cached billing plan (no extra
 * request). Fires `Feature Gate Hit` on the denial path, deduplicated
 * per (workspace, feature) per mount so a card that re-renders many
 * times only counts once.
 *
 * Pass `{ track: false }` when calling purely for conditional rendering —
 * e.g. hiding a nav or command-palette entry, or hooks where a click-handler already
 * fires the intent signal. This prevents passive renders from inflating
 * gate-hit counts in analytics.
 *
 * Branch on `allowed` to gate UI; `requiredTier` is the upgrade target
 * for composing upsell copy.
 */
export function useCanUseFeature(
  feature: TierFeature,
  { track = true }: UseCanUseFeatureOptions = {},
): FeatureGateResult {
  const { data: plan, isLoading } = useWorkspacePlan();
  const workspaceId = useWorkspaceId();
  const currentTier = (plan?.tier ?? 'FREE') as SubscriptionTier;
  const requiredTier = minimumTierForFeature(feature);
  // Read the per-tier boolean directly from TIER_POLICY rather than
  // assuming strict tier-ladder monotonicity. `tierAtLeast` would
  // incorrectly return true if a feature is ever PRO-only (off on
  // TEAM). The backend uses the same primitive.
  const allowed = tierIncludesFeature(currentTier, feature);

  // Telemetry on denial — fire once per (feature, workspace) for the
  // lifetime of this hook instance. Without dedup a usage card visible
  // for 30 seconds spams the funnel.
  //
  // Keyed by `feature` so the dedup resets when the caller passes a
  // different TierFeature key into the same hook instance (e.g. a tab
  // switch). If we only used a boolean ref, a hook reused for two
  // features would silently drop the second feature's denial event.
  const lastTrackedRef = useRef<TierFeature | null>(null);
  useEffect(() => {
    if (!track || isLoading || allowed) return;
    if (lastTrackedRef.current === feature) return;
    lastTrackedRef.current = feature;
    const gateLayer: GateLayer = 'frontend';
    safeAnalytics.track(ANALYTICS_EVENTS.FEATURE_GATE_HIT, {
      feature_key: feature,
      current_tier: currentTier,
      required_tier: requiredTier,
      gate_layer: gateLayer,
      workspace_id: workspaceId ?? null,
    } as never);
  }, [track, isLoading, allowed, feature, currentTier, requiredTier, workspaceId]);

  return { allowed, currentTier, requiredTier, isLoading };
}
